import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { Chart as ChartJS, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import './Page2.css'; // Assuming you have a CSS file for styling

ChartJS.register(...registerables, zoomPlugin);

// --- Modal Component ---
const AISummaryModal = ({ summary, onClose }) => {
  // If summary is empty string, we should still let it render if isModalOpen is true
  // to display an error message if needed, but the main component handles the visibility.
  
  const formattedSummary = summary.split('\n').map((line, index) => {
    if (line.trim().startsWith('**')) {
      const cleanLine = line.replace(/\*\*/g, '').trim();
      return <p key={index} style={{ fontWeight: 'bold', marginTop: '10px' }}>{cleanLine}</p>;
    }
    if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
        return <li key={index} style={{ marginLeft: '20px' }}>{line.slice(1).trim()}</li>
    }
    return <p key={index}>{line}</p>;
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2>🔮 Sensor AI Summary & Predictions</h2>
        <div className="modal-body">
          {formattedSummary}
        </div>
        <button onClick={onClose} className="modal-close-button">Close</button>
      </div>
    </div>
  );
};

// --- Electricity AI Modal Component ---
const ElectricityAISummaryModal = ({ summary, onClose }) => {
  const formattedSummary = summary.split('\n').map((line, index) => {
    if (line.trim().startsWith('**')) {
      const cleanLine = line.replace(/\*\*/g, '').trim();
      return <p key={index} style={{ fontWeight: 'bold', marginTop: '10px' }}>{cleanLine}</p>;
    }
    if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
        return <li key={index} style={{ marginLeft: '20px' }}>{line.slice(1).trim()}</li>
    }
    return <p key={index}>{line}</p>;
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2>⚡ Electricity AI Summary & Predictions</h2>
        <div className="modal-body">
          {formattedSummary}
        </div>
        <button onClick={onClose} className="modal-close-button">Close</button>
      </div>
    </div>
  );
};


function Page2() {
  const [data, setData] = useState({});
  const [noise, setNoise] = useState({});
  const [co2, setCo2] = useState({});
  const [humidity, setHumidity] = useState({});
  const [pressure, setPressure] = useState({});
  const [electricity, setElectricity] = useState({});
  const [river, setRiver] = useState({});
  const [aiSummary, setAiSummary] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAISummaryLoading, setIsAISummaryLoading] = useState(false);
  const [electricityAiSummary, setElectricityAiSummary] = useState('');
  const [isElectricityModalOpen, setIsElectricityModalOpen] = useState(false);
  const [isElectricityAISummaryLoading, setIsElectricityAISummaryLoading] = useState(false);
  const [numberItems, setNumberItems] = useState(150);
  const [selectedDevice, setSelectedDevice] = useState("Downstairs speaker");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('temperature');
  const [showAllCharts, setShowAllCharts] = useState(false);
  const [isDeviceSelectorOpen, setIsDeviceSelectorOpen] = useState(false);

  // Centralized data fetching utility
  const fetchData = async (url, setter) => {
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: 'kSuIlUmNaTiIeO5',
        },
        params: {
          number_of_items: numberItems,
        },
      });
      setter(response.data);
    } catch (error) {
      console.error(`Error fetching data from ${url}: ${error}`);
    }
  };

  useEffect(() => {
    fetchData('https://kidr78home.duckdns.org/api/temps', setData);
    fetchData('https://kidr78home.duckdns.org/api/noise', setNoise);
    fetchData('https://kidr78home.duckdns.org/api/co2', setCo2);
    fetchData('https://kidr78home.duckdns.org/api/humidity', setHumidity);
    fetchData('https://kidr78home.duckdns.org/api/pressure', setPressure);
    fetchData('https://kidr78home.duckdns.org/api/sahko', setElectricity);
    fetchData('https://kidr78home.duckdns.org/api/river', setRiver);
  }, [numberItems]);
  const fetchAISummary = useCallback(async () => {
    // If the modal is already open and summary data exists, just close it.
    // This is optional, but often a good UX practice.
    if (isModalOpen) {
        setIsModalOpen(false);
        return;
    }
    
    setIsAISummaryLoading(true);
    const url = 'https://kidr78home.duckdns.org/api/sensorsai';
    
    // Using native fetch API 
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': 'kSuIlUmNaTiIeO5',
        },
      });

      if (!response.ok) {
        throw new Error(`AI Summary HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.aisummary) {
        setAiSummary(data.aisummary);
        setIsModalOpen(true); // Open the modal only on successful fetch
      } else {
        setAiSummary('Error: AI Summary field not found in response.');
        setIsModalOpen(true);
      }

    } catch (error) {
      console.error(`Error fetching AI Summary: ${error}`);
      setAiSummary(`Failed to retrieve AI Summary. Error: ${error.message}`);
      setIsModalOpen(true);
    } finally {
      setIsAISummaryLoading(false);
    }
  }, [isModalOpen]); // Depend on isModalOpen for the UX improvement above

  const fetchElectricityAISummary = useCallback(async () => {
    // If the modal is already open and summary data exists, just close it.
    if (isElectricityModalOpen) {
        setIsElectricityModalOpen(false);
        return;
    }
    
    setIsElectricityAISummaryLoading(true);
    const url = 'https://kidr78home.duckdns.org/api/sensorsaisahko';
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': 'kSuIlUmNaTiIeO5',
        },
      });

      if (!response.ok) {
        throw new Error(`Electricity AI Summary HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.aisummary) {
        setElectricityAiSummary(data.aisummary);
        setIsElectricityModalOpen(true); // Open the modal only on successful fetch
      } else {
        setElectricityAiSummary('Error: Electricity AI Summary field not found in response.');
        setIsElectricityModalOpen(true);
      }

    } catch (error) {
      console.error(`Error fetching Electricity AI Summary: ${error}`);
      setElectricityAiSummary(`Failed to retrieve Electricity AI Summary. Error: ${error.message}`);
      setIsElectricityModalOpen(true);
    } finally {
      setIsElectricityAISummaryLoading(false);
    }
  }, [isElectricityModalOpen]);

  const castToDevice = () => {
    const apiUrl = `https://kidr78home.duckdns.org/api/casttemps?device_name=${encodeURIComponent(selectedDevice)}`;
    setIsLoading(true);

    fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: "kSuIlUmNaTiIeO5",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to call the API");
        }
        return response.json();
      })
      .then((data) => {
        console.log("API Response:", data);
      })
      .catch((error) => {
        console.error("Error calling API:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Enhanced color palette with better contrast and visibility
  const colorPalette = [
    '#E74C3C', // Strong Red
    '#3498DB', // Strong Blue  
    '#2ECC71', // Strong Green
    '#F39C12', // Strong Orange
    '#9B59B6', // Strong Purple
    '#1ABC9C', // Strong Teal
    '#E67E22', // Strong Dark Orange
    '#34495E', // Strong Dark Blue-Gray
    '#16A085', // Strong Dark Teal
    '#8E44AD', // Strong Dark Purple
    '#D35400', // Strong Dark Orange-Red
    '#2C3E50'  // Strong Dark Navy
  ];

  const labels = Object.keys(data);
  const datasets = labels.length > 0 && data[labels[0]] ? Object.entries(data[labels[0]]).map(([place, temp], index) => ({
    label: place,
    data: labels.map((label) => data[label][place]),
    fill: false,
    borderColor: colorPalette[index % colorPalette.length],
    backgroundColor: colorPalette[index % colorPalette.length] + '20',
    pointBackgroundColor: colorPalette[index % colorPalette.length],
    pointBorderColor: colorPalette[index % colorPalette.length],
    pointBorderWidth: 2,
    borderWidth: 3,
    pointRadius: 4,
    pointHoverRadius: 6,
  })) : [];

  const chartData = {
    labels,
    datasets,
  };
  // Enhanced chart options with better mobile support
  const getChartOptions = (title, yAxisLabel, enableZoom = true, dualAxis = false) => {
    const isRiver = title.includes('Siuntionjoki');
    const y1Label = isRiver ? 'Water Temp (°C)' : 'Est. Monthly kWh';
    const y1Color = isRiver ? '#E74C3C' : '#F39C12';

    return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: 20
      },
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context) {
            const date = new Date(context[0].label);
            return date.toLocaleString();
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} ${yAxisLabel}`;
          }
        }
      },
      ...(enableZoom && {
        zoom: {
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            mode: 'x',
            speed: 0.1
          },
          pan: {
            enabled: true,
            mode: 'x',
            speed: 0.5
          },
          limits: {
            x: { min: 'original', max: 'original' }
          }
        }
      })
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        ticks: {
          maxTicksLimit: window.innerWidth < 768 ? 6 : 12,
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11
          },
          callback: function(value, index) {
            const date = new Date(this.getLabelForValue(value));
            if (window.innerWidth < 768) {
              return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            return date.toLocaleString([], { 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            });
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        }
      },
      y: {
        title: {
          display: true,
          text: yAxisLabel,
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        ticks: {
          font: {
            size: 11
          },
          callback: function(value) {
            return value.toFixed(1);
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        }
      },
      ...(dualAxis && {
        y1: {
          position: 'right',
          title: {
            display: true,
            text: y1Label,
            font: { size: 12, weight: 'bold' },
            color: y1Color,
          },
          ticks: {
            font: { size: 10 },
            color: y1Color,
            callback: function(value) { return value.toFixed(1); }
          },
          grid: { drawOnChartArea: false },
        }
      })
    },
    elements: {
      point: {
        radius: window.innerWidth < 768 ? 3 : 4,
        hoverRadius: window.innerWidth < 768 ? 5 : 7,
        borderWidth: 2,
        backgroundColor: function(context) {
          return context.dataset.pointBackgroundColor;
        },
        borderColor: function(context) {
          return context.dataset.pointBorderColor;
        }
      },
      line: {
        borderWidth: window.innerWidth < 768 ? 3 : 4,
        tension: 0.1
      }
    }
  };};

  // Generate enhanced chart data for all sensor types
  const generateChartData = (dataObj, colorOffset = 0) => {
    const labels = Object.keys(dataObj);
    if (labels.length === 0 || !dataObj[labels[0]]) return { labels: [], datasets: [] };
    
    const datasets = Object.entries(dataObj[labels[0]]).map(([place, value], index) => ({
      label: place,
      data: labels.map((label) => dataObj[label][place]),
      fill: false,
      borderColor: colorPalette[(index + colorOffset) % colorPalette.length],
      backgroundColor: colorPalette[(index + colorOffset) % colorPalette.length] + '20',
      pointBackgroundColor: colorPalette[(index + colorOffset) % colorPalette.length],
      pointBorderColor: colorPalette[(index + colorOffset) % colorPalette.length],
      pointBorderWidth: 2,
      borderWidth: 3,
      pointRadius: 4,
      pointHoverRadius: 6,
    }));

    return { labels, datasets };
  };

  const chartDataNoise = generateChartData(noise, 2);
  const chartDataCO2 = generateChartData(co2, 3);
  const chartDataHumidity = generateChartData(humidity, 4);
  const chartDataPressure = generateChartData(pressure, 5);

  // Electricity needs special handling — different fields have very different scales
  const generateElectricityChart = () => {
    const labels = Object.keys(electricity);
    if (labels.length === 0) return { labels: [], datasets: [] };

    return {
      labels,
      datasets: [
        {
          label: 'Total kWh',
          data: labels.map(l => electricity[l]?.total_kw ?? null),
          fill: false,
          borderColor: '#E74C3C',
          backgroundColor: '#E74C3C20',
          pointBackgroundColor: '#E74C3C',
          pointBorderColor: '#E74C3C',
          pointBorderWidth: 2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'House Only kWh',
          data: labels.map(l => electricity[l]?.kw_without_carcharge ?? null),
          fill: false,
          borderColor: '#3498DB',
          backgroundColor: '#3498DB20',
          pointBackgroundColor: '#3498DB',
          pointBorderColor: '#3498DB',
          pointBorderWidth: 2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Car Charging kWh',
          data: labels.map(l => electricity[l]?.car_charge_total ?? null),
          fill: false,
          borderColor: '#2ECC71',
          backgroundColor: '#2ECC7120',
          pointBackgroundColor: '#2ECC71',
          pointBorderColor: '#2ECC71',
          pointBorderWidth: 2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Est. Monthly kWh',
          data: labels.map(l => electricity[l]?.estimated_per_month ?? null),
          fill: false,
          borderColor: '#F39C12',
          backgroundColor: '#F39C1210',
          pointBackgroundColor: '#F39C12',
          pointBorderColor: '#F39C12',
          pointBorderWidth: 1,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 2,
          pointHoverRadius: 4,
          yAxisID: 'y1',
        },
      ],
    };
  };

  const chartDataElectricity = generateElectricityChart();

  // River data — dual axis: depth + current on left, water temp on right
  const generateRiverChart = () => {
    const labels = Object.keys(river);
    if (labels.length === 0) return { labels: [], datasets: [] };

    return {
      labels,
      datasets: [
        {
          label: 'Depth (m)',
          data: labels.map(l => river[l]?.river_depth_m ?? null),
          fill: false,
          borderColor: '#3498DB',
          backgroundColor: '#3498DB20',
          pointBackgroundColor: '#3498DB',
          pointBorderColor: '#3498DB',
          pointBorderWidth: 2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Current (m/s)',
          data: labels.map(l => river[l]?.river_current_ms ?? null),
          fill: false,
          borderColor: '#2ECC71',
          backgroundColor: '#2ECC7120',
          pointBackgroundColor: '#2ECC71',
          pointBorderColor: '#2ECC71',
          pointBorderWidth: 2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Water Temp (°C)',
          data: labels.map(l => river[l]?.water_temp_c ?? null),
          fill: false,
          borderColor: '#E74C3C',
          backgroundColor: '#E74C3C20',
          pointBackgroundColor: '#E74C3C',
          pointBorderColor: '#E74C3C',
          pointBorderWidth: 2,
          borderWidth: 2,
          borderDash: [5, 3],
          pointRadius: 3,
          pointHoverRadius: 5,
          yAxisID: 'y1',
        },
      ],
    };
  };

  const chartDataRiver = generateRiverChart();


  // Get current values for overview cards
  const getCurrentValues = () => {
    // Show loading state if no data is available yet
    if (Object.keys(data).length === 0) {
      return {
        temperature: {},
        noise: {},
        co2: {},
        humidity: {},
        pressure: {},
        electricity: {},
        river: {}
      };
    }

    const latest = {
      temperature: {},
      noise: {},
      co2: {},
      humidity: {},
      pressure: {},
      electricity: {},
      river: {}
    };

    if (Object.keys(data).length > 0) {
      const latestTempKey = Object.keys(data)[Object.keys(data).length - 1];
      latest.temperature = data[latestTempKey] || {};
    }

    if (Object.keys(noise).length > 0) {
      const latestNoiseKey = Object.keys(noise)[Object.keys(noise).length - 1];
      latest.noise = noise[latestNoiseKey] || {};
    }

    if (Object.keys(co2).length > 0) {
      const latestCO2Key = Object.keys(co2)[Object.keys(co2).length - 1];
      latest.co2 = co2[latestCO2Key] || {};
    }

    if (Object.keys(humidity).length > 0) {
      const latestHumidityKey = Object.keys(humidity)[Object.keys(humidity).length - 1];
      latest.humidity = humidity[latestHumidityKey] || {};
    }

    if (Object.keys(pressure).length > 0) {
      const latestPressureKey = Object.keys(pressure)[Object.keys(pressure).length - 1];
      latest.pressure = pressure[latestPressureKey] || {};
    }

    if (Object.keys(electricity).length > 0) {
      const latestElectricityKey = Object.keys(electricity)[Object.keys(electricity).length - 1];
      const electricityData = electricity[latestElectricityKey] || {};
      latest.electricity = {
        'Total': electricityData.total_kw != null ? `${electricityData.total_kw.toFixed(1)} kWh` : 'N/A',
        'House': electricityData.kw_without_carcharge != null ? `${electricityData.kw_without_carcharge.toFixed(1)} kWh` : 'N/A',
        'Car': electricityData.car_charge_total != null ? `${electricityData.car_charge_total.toFixed(1)} kWh` : 'N/A',
        'Est/Month': electricityData.estimated_per_month != null ? `${electricityData.estimated_per_month.toFixed(0)} kWh` : 'N/A',
        'Est Cost': electricityData.estimated_per_month != null ? `€${(electricityData.estimated_per_month * 0.18).toFixed(0)}` : 'N/A',
      };
    }

    if (Object.keys(river).length > 0) {
      const latestRiverKey = Object.keys(river)[Object.keys(river).length - 1];
      const riverData = river[latestRiverKey] || {};
      latest.river = {
        'Depth': riverData.river_depth_m != null ? `${riverData.river_depth_m.toFixed(2)} m` : 'N/A',
        'Current': riverData.river_current_ms != null ? `${riverData.river_current_ms.toFixed(1)} m/s` : 'N/A',
        'Water Temp': riverData.water_temp_c != null ? `${riverData.water_temp_c.toFixed(1)} °C` : 'N/A',
      };
    }

    return latest;
  };

  const currentValues = getCurrentValues();

  const chartTabs = [
    { id: 'temperature', label: '🌡️ Temperature', unit: '°C', data: chartData, icon: '🌡️' },
    { id: 'electricity', label: '⚡ Electricity', unit: 'kWh', data: chartDataElectricity, icon: '⚡' },
    { id: 'river', label: '🏞️ Siuntionjoki', unit: 'm', data: chartDataRiver, icon: '🏞️' },
    { id: 'noise', label: '🔊 Noise', unit: 'dB', data: chartDataNoise, icon: '🔊' },
    { id: 'co2', label: '💨 CO2', unit: 'ppm', data: chartDataCO2, icon: '💨' },
    { id: 'humidity', label: '💧 Humidity', unit: '%', data: chartDataHumidity, icon: '💧' },
    { id: 'pressure', label: '🌪️ Pressure', unit: 'hPa', data: chartDataPressure, icon: '🌪️' }
  ];

  // --- Render ---
  // Show loading state if no data is available yet
  if (Object.keys(data).length === 0 || 
      Object.keys(noise).length === 0 || 
      Object.keys(co2).length === 0 || 
      Object.keys(humidity).length === 0 || 
      Object.keys(pressure).length === 0 ||
      Object.keys(electricity).length === 0) {
    return (
      <div className="sensors-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading sensor data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sensors-page">
      {/* Collapsible Device Selector */}
      <div className={`device-selector-container ${isDeviceSelectorOpen ? 'open' : ''}`}>
        <button 
          className="device-selector-toggle"
          onClick={() => setIsDeviceSelectorOpen(!isDeviceSelectorOpen)}
        >
          📱 Cast Controls {isDeviceSelectorOpen ? '▼' : '▶'}
        </button>
        
        {isDeviceSelectorOpen && (
          <div className="device-selector-content">
            <div className="device-selector-row">
              <label htmlFor="deviceSelect">Choose a device:</label>
              <select
                id="deviceSelect"
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
              >
                <option value="Downstairs speaker">Downstairs speaker</option>
                <option value="bedroom speaker">Bedroom speaker</option>
                <option value="Valery Studio Bed">Valery Studio</option>
              </select>
            </div>
            
            <div className="device-selector-buttons">
              <button
                onClick={castToDevice}
                className={`cast-button ${isLoading ? "blinking" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? "Casting..." : "Cast to Device"}
              </button>

              <button 
                onClick={fetchAISummary} 
                className={`ai-summary-button ${isAISummaryLoading ? "blinking" : ""}`}
                disabled={isAISummaryLoading}
              >
                {isAISummaryLoading ? "Analyzing..." : "View Sensor AI 🧠"}
              </button>

              <button 
                onClick={fetchElectricityAISummary} 
                className={`ai-summary-button electricity-ai ${isElectricityAISummaryLoading ? "blinking" : ""}`}
                disabled={isElectricityAISummaryLoading}
              >
                {isElectricityAISummaryLoading ? "Analyzing..." : "View Electricity AI ⚡"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Current Values Overview */}
      <div className="overview-section">
        <h2 className="overview-title">📊 Current Sensor & Energy Readings</h2>
        <div className="overview-cards">
          {chartTabs.map(tab => (
            <div key={tab.id} className="overview-card">
              <div className="card-header">
                <span className="card-icon">{tab.icon}</span>
                <span className="card-title">{tab.label.replace(/🌡️|🔊|💨|💧|🌪️/g, '').trim()}</span>
              </div>
              <div className="card-values">
                {Object.entries(currentValues[tab.id] || {}).map(([location, value]) => (
                  <div key={location} className="value-item">
                    <span className="location">{location}:</span>
                    <span className="value">{typeof value === 'number' ? value.toFixed(1) : value} {tab.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart Controls */}
      <div className="chart-controls">
        <div className="chart-tabs">
          {chartTabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        <div className="view-controls">
          <button
            className={`view-toggle ${showAllCharts ? 'active' : ''}`}
            onClick={() => setShowAllCharts(!showAllCharts)}
          >
            {showAllCharts ? '📊 Single View' : '📈 All Charts'}
          </button>
        </div>
      </div>

      {/* Data Range Control */}
      <div className="data-controls">
        <div className="range-control">
          <label htmlFor="numberItems">📅 Data Points: {numberItems}</label>
          <input
            type="range"
            min="50"
            max="10000"
            step="50"
            id="numberItems"
            value={numberItems}
            onChange={(e) => setNumberItems(e.target.value)}
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {showAllCharts ? (
          // Show all charts in a grid
          <div className="charts-grid">
            {chartTabs.map(tab => (
              <div key={tab.id} className="chart-container">
                <Line 
                  data={tab.data} 
                  options={getChartOptions(tab.label, tab.unit, true, tab.id === 'electricity' || tab.id === 'river')} 
                />
              </div>
            ))}
          </div>
        ) : (
          // Show single active chart
          <div className="single-chart-container">
            {(() => {
              const activeTabData = chartTabs.find(tab => tab.id === activeTab);
              return activeTabData ? (
                <Line 
                  data={activeTabData.data} 
                  options={getChartOptions(activeTabData.label, activeTabData.unit, true, activeTab === 'electricity' || activeTab === 'river')} 
                />
              ) : null;
            })()}
          </div>
        )}
      </div>

      {/* AI Summary Modal */}
      {isModalOpen && (
        <AISummaryModal
          summary={aiSummary}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {/* Electricity AI Summary Modal */}
      {isElectricityModalOpen && (
        <ElectricityAISummaryModal
          summary={electricityAiSummary}
          onClose={() => setIsElectricityModalOpen(false)}
        />
      )}
    </div>
  );
}

export default Page2;
